import React, { useState, useRef } from 'react';
import { Globe, CheckCircle, XCircle, Clock, AlertCircle, Printer, Shield, Zap, Search, ChevronDown, ChevronUp, Pencil, Check, X, Download } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import { Button } from '../ui/Button';
import { useWebsiteAssessment, useTriggerWebsiteAssessment } from '../../hooks/useWebsiteAssessment';
import { useCurrentCompany } from '../../hooks/useCurrentCompany';
import { supabase } from '../../lib/supabase';
import { FeatureRequestButton } from './FeatureRequestButton';
import { WebsiteAssessmentPrint } from './WebsiteAssessmentPrint';
import html2pdf from 'html2pdf.js';

export function WebsiteStatusSection() {
  const { data: companyId } = useCurrentCompany();
  const { data: assessment, isLoading } = useWebsiteAssessment();
  const triggerAssessment = useTriggerWebsiteAssessment();
  const [websiteUrl, setWebsiteUrl] = useState<string | null>(null);
  const [checkingUrl, setCheckingUrl] = useState(false);
  const [companyName, setCompanyName] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'basic' | 'features' | 'seo'>('basic');
  const [isExpanded, setIsExpanded] = useState(false);
  const [isRequestingConsultation, setIsRequestingConsultation] = useState(false);
  const [isEditingUrl, setIsEditingUrl] = useState(false);
  const [editedUrl, setEditedUrl] = useState<string>('');
  const [isSavingUrl, setIsSavingUrl] = useState(false);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const printRef = useRef<HTMLDivElement>(null);

  // Get website URL and company name from company
  React.useEffect(() => {
    const fetchCompanyData = async () => {
      if (!companyId) return;
      try {
        const { data, error } = await (supabase as any)
          .from('companies')
          .select('website_url, name')
          .eq('id', companyId)
          .single();
        
        if (error) {
          // If column doesn't exist (migration not applied), handle gracefully
          if (error.code === 'PGRST116' || error.message?.includes('does not exist') || error.message?.includes('column')) {
            console.warn('website_url column not found. Migrations may not be applied.');
            // Set defaults
            setWebsiteUrl(null);
            // Try to get name without website_url
            const { data: nameData } = await (supabase as any)
              .from('companies')
              .select('name')
              .eq('id', companyId)
              .single();
            setCompanyName((nameData as any)?.name || null);
          } else {
            console.error('Error fetching company data:', error);
          }
        } else {
          setWebsiteUrl((data as any)?.website_url || null);
          setCompanyName((data as any)?.name || null);
        }
      } catch (error: any) {
        // Handle column not found gracefully
        if (error?.code === 'PGRST116' || error?.message?.includes('does not exist') || error?.message?.includes('column')) {
          console.warn('website_url column not found. Migrations may not be applied.');
          setWebsiteUrl(null);
        } else {
          console.error('Error fetching company data:', error);
        }
      }
    };
    fetchCompanyData();
  }, [companyId]);

  const handleCheckWebsite = async () => {
    if (!websiteUrl || !companyId) return;
    try {
      setCheckingUrl(true);
      await triggerAssessment.mutateAsync(websiteUrl);
    } catch (error) {
      console.error('Error triggering website assessment:', error);
      // Show user-friendly error message
      const errorMessage = error instanceof Error ? error.message : 'Failed to assess website';
      alert(`Website assessment failed: ${errorMessage}\n\nThis could be due to:\n- Invalid or unreachable URL\n- Website blocking automated requests\n- Network connectivity issues\n- Edge Function timeout\n\nPlease verify the URL is correct and try again.`);
    } finally {
      setCheckingUrl(false);
    }
  };

  const handleRequestConsultation = async () => {
    if (!companyId) return;
    try {
      setIsRequestingConsultation(true);
      const { data, error } = await supabase.functions.invoke('request-website-consultation', {
        body: {
          company_id: companyId,
          company_name: companyName,
          website_url: websiteUrl,
          source: 'website-assessment',
        },
      });

      if (error) {
        throw error;
      }

      if (data?.error) {
        throw new Error(data.error);
      }

      alert('Consultation request submitted! Our team will reach out to schedule a time that works for you.');
    } catch (error) {
      console.error('Error requesting consultation:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to submit request';
      alert(`Failed to submit consultation request: ${errorMessage}\n\nPlease try again or contact support@rollops.pro directly.`);
    } finally {
      setIsRequestingConsultation(false);
    }
  };

  const handleEditUrl = () => {
    setEditedUrl(websiteUrl || '');
    setIsEditingUrl(true);
  };

  const handleSaveUrl = async () => {
    if (!companyId || !editedUrl.trim()) return;
    
    try {
      setIsSavingUrl(true);
      let normalizedUrl = editedUrl.trim();
      if (!normalizedUrl.startsWith('http://') && !normalizedUrl.startsWith('https://')) {
        normalizedUrl = 'https://' + normalizedUrl;
      }

      // Validate URL format
      try {
        new URL(normalizedUrl);
      } catch (urlError) {
        alert('Invalid URL format. Please enter a valid URL (e.g., https://example.com)');
        setIsSavingUrl(false);
        return;
      }

      // Update company website URL
      const { error } = await (supabase as any)
        .from('companies')
        .update({ website_url: normalizedUrl })
        .eq('id', companyId);

      if (error) {
        throw error;
      }

      setWebsiteUrl(normalizedUrl);
      setIsEditingUrl(false);
      
      // Trigger new assessment with updated URL
      await triggerAssessment.mutateAsync(normalizedUrl);
    } catch (error) {
      console.error('Error updating website URL:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to save website URL';
      alert(`Failed to save website URL: ${errorMessage}\n\nPlease try again.`);
    } finally {
      setIsSavingUrl(false);
    }
  };

  const handleCancelEditUrl = () => {
    setIsEditingUrl(false);
    setEditedUrl('');
  };

  const getScore = () => {
    if (!assessment) return { score: 0, totalChecks: 0 };
    let score = 0;
    let totalChecks = 0;
    
    // Basic checks
    if (assessment.url_works) score++;
    totalChecks++;
    if (assessment.loads_successfully) score++;
    totalChecks++;
    if (assessment.has_phone_number) score++;
    totalChecks++;
    if (assessment.has_email) score++;
    totalChecks++;
    if (assessment.has_address) score++;
    totalChecks++;
    
    // Additional checks
    if (assessment.has_ssl) score++;
    totalChecks++;
    if (assessment.mobile_friendly) score++;
    totalChecks++;
    if (assessment.has_social_links) score++;
    totalChecks++;
    if (assessment.has_online_booking) score++;
    totalChecks++;
    if (assessment.has_meta_title) score++;
    totalChecks++;
    if (assessment.has_meta_description) score++;
    totalChecks++;
    
    return { score, totalChecks };
  };

  const getRecommendation = () => {
    if (!assessment) return '';
    
    const { score, totalChecks } = getScore();
    const percentage = totalChecks > 0 ? Math.round((score / totalChecks) * 100) : 0;
    
    const issues: string[] = [];
    
    if (!assessment.url_works || !assessment.loads_successfully) {
      issues.push('Your website may be experiencing connectivity issues or timeouts');
    }
    if (!assessment.has_phone_number) {
      issues.push('add a visible phone number');
    }
    if (!assessment.has_email) {
      issues.push('include contact email');
    }
    if (!assessment.has_address) {
      issues.push('display your business address');
    }
    if (!assessment.has_ssl) {
      issues.push('enable HTTPS/SSL security');
    }
    if (!assessment.mobile_friendly) {
      issues.push('optimize for mobile devices');
    }
    if (!assessment.has_online_booking) {
      issues.push('add online booking or scheduling');
    }
    
    if (percentage >= 80) {
      return `Your website is performing well (${percentage}% score). ${issues.length > 0 ? `Consider: ${issues.slice(0, 2).join(', ')}.` : 'Keep up the great work!'}`;
    } else if (percentage >= 60) {
      return `Your website has room for improvement (${percentage}% score). Focus on: ${issues.slice(0, 3).join(', ')}.`;
    } else {
      return `Your website needs attention (${percentage}% score). Priority fixes: ${issues.slice(0, 4).join(', ')}.`;
    }
  };
  
  const handlePrint = () => {
    // Set document title for PDF filename
    const originalTitle = document.title;
    const dateStr = assessment ? new Date(assessment.assessment_date).toISOString().split('T')[0] : new Date().toISOString().split('T')[0];
    const filename = `RollOps-Free-Website-Assessment-${dateStr}`;
    document.title = filename;
    
    window.print();
    
    // Restore original title after a delay
    setTimeout(() => {
      document.title = originalTitle;
    }, 1000);
  };

  const handleDownloadPdf = async () => {
    if (!assessment || !websiteUrl || !companyId || !printRef.current) {
      return;
    }

    setIsGeneratingPdf(true);
    try {
      const printElement = printRef.current;
      
      // Generate PDF using html2pdf.js
      const opt = {
        margin: 0.5,
        filename: `RollOps-Free-Website-Assessment-${new Date(assessment.assessment_date).toISOString().split('T')[0]}.pdf`,
        image: { type: 'jpeg' as const, quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true },
        jsPDF: { unit: 'in', format: 'letter', orientation: 'portrait' as const },
      };

      const pdfBlob = await html2pdf().set(opt).from(printElement).outputPdf('blob');
      
      // Convert blob to base64
      const reader = new FileReader();
      const base64Promise = new Promise<string>((resolve, reject) => {
        reader.onloadend = () => {
          const base64String = reader.result as string;
          resolve(base64String);
        };
        reader.onerror = reject;
      });
      reader.readAsDataURL(pdfBlob);
      const pdfBase64 = await base64Promise;

      // Get session for authentication
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('Not authenticated');
      }

      // Call edge function to upload PDF and get signed URL
      const { data: edgeFunctionData, error: edgeError } = await supabase.functions.invoke(
        'generate-website-assessment-pdf',
        {
          body: {
            pdfBase64,
            companyId,
            assessmentId: assessment.id,
            websiteUrl,
          },
        }
      );

      if (edgeError || !edgeFunctionData?.signedUrl) {
        throw new Error(edgeError?.message || 'Failed to generate PDF URL');
      }

      // Determine base URL for viewer page
      // Use current origin (works for both localhost and production)
      const baseUrl = typeof window !== 'undefined' 
        ? window.location.origin 
        : 'https://rollops.pro';
      
      // Open PDF in viewer page with absolute URL
      const viewerUrl = `${baseUrl}/view-assessment?url=${encodeURIComponent(edgeFunctionData.signedUrl)}`;
      window.open(viewerUrl, '_blank');
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert(`Failed to generate PDF: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  const getStatusColor = (percentage: number) => {
    if (percentage >= 80) return 'text-green-400';
    if (percentage >= 60) return 'text-yellow-400';
    return 'text-red-400';
  };

  const getStatusIndicator = (percentage: number) => {
    // If assessment failed due to connection/timeout, show warning instead of red
    if (assessment?.error_message && !assessment.url_works) {
      return '🟡'; // Warning for connection issues (might be URL typo)
    }
    if (percentage >= 80) return '🟢';
    if (percentage >= 60) return '🟡';
    return '🔴';
  };

  const { score, totalChecks } = getScore();
  const percentage = totalChecks > 0 ? Math.round((score / totalChecks) * 100) : 0;

  return (
    <Card className="bg-[#221F1F] border-[#504A4A]">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-text-primary flex items-center gap-2">
            <Globe className="w-5 h-5 text-rollops-accent" />
            Website Assessment
          </CardTitle>
          {websiteUrl && !isEditingUrl && (
            <div className="flex items-center gap-2">
              <span className="text-sm text-text-secondary truncate max-w-md" title={websiteUrl}>
                {websiteUrl}
              </span>
              <Button
                onClick={handleEditUrl}
                className="bg-[#363232] hover:bg-[#504A4A] text-text-primary p-1.5 h-auto"
                size="sm"
                title="Edit Website URL"
              >
                <Pencil className="w-4 h-4" />
              </Button>
            </div>
          )}
          {isEditingUrl && (
            <div className="flex items-center gap-2 flex-1 max-w-md">
              <input
                type="text"
                value={editedUrl}
                onChange={(e) => setEditedUrl(e.target.value)}
                className="flex-1 px-3 py-1.5 bg-[#363232] border border-[#504A4A] rounded text-text-primary text-sm focus:outline-none focus:ring-2 focus:ring-[#9B1421]"
                placeholder="https://example.com"
                maxLength={2048}
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleSaveUrl();
                  } else if (e.key === 'Escape') {
                    handleCancelEditUrl();
                  }
                }}
              />
              <Button
                onClick={handleSaveUrl}
                disabled={isSavingUrl || !editedUrl.trim()}
                className="bg-[#9B1421] hover:bg-[#7A0F1A] text-white p-1.5 h-auto"
                size="sm"
                title="Save URL"
              >
                {isSavingUrl ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                ) : (
                  <Check className="w-4 h-4" />
                )}
              </Button>
              <Button
                onClick={handleCancelEditUrl}
                disabled={isSavingUrl}
                className="bg-[#363232] hover:bg-[#504A4A] text-text-primary p-1.5 h-auto"
                size="sm"
                title="Cancel"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {!websiteUrl ? (
          <div className="text-center py-8 text-text-secondary">
            <p className="mb-2">No website URL configured</p>
            <p className="text-sm text-text-tertiary mb-4">
              Add your website URL to enable assessment
            </p>
            <Button
              onClick={() => {
                const url = prompt('Enter your website URL:', 'https://');
                if (url && url.trim()) {
                  let normalizedUrl = url.trim();
                  if (!normalizedUrl.startsWith('http://') && !normalizedUrl.startsWith('https://')) {
                    normalizedUrl = 'https://' + normalizedUrl;
                  }
                  // Update company website URL
                  (async () => {
                    if (!companyId) return;
                    try {
                      const { error } = await (supabase as any)
                        .from('companies')
                        .update({ website_url: normalizedUrl })
                        .eq('id', companyId);
                      if (error) throw error;
                      setWebsiteUrl(normalizedUrl);
                      // Trigger assessment
                      await triggerAssessment.mutateAsync(normalizedUrl);
                    } catch (error) {
                      console.error('Error updating website URL:', error);
                      alert('Failed to save website URL. Please try again.');
                    }
                  })();
                }
              }}
              className="bg-[#9B1421] hover:bg-[#7A0F1A] text-white text-sm"
            >
              <Globe className="w-4 h-4 mr-2 inline" />
              Add Website URL
            </Button>
          </div>
        ) : isLoading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#9B1421]" />
          </div>
        ) : assessment ? (
          <>
            {/* Collapsed Summary */}
            <div className="mb-4 p-4 bg-[#363232] rounded-lg">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xl">{getStatusIndicator(percentage)}</span>
                    <span className={`text-lg font-bold ${getStatusColor(percentage)}`}>
                      Website Score: {score}/{totalChecks} ({percentage}%)
                    </span>
                    {assessment.load_time_ms && (
                      <span className="text-xs text-text-secondary">
                        • {(assessment.load_time_ms / 1000).toFixed(1)}s load time
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-text-primary mb-2">
                    {getRecommendation()}
                  </p>
                  {assessment.ai_summary && (
                    <div className="mt-3 p-3 bg-[#9B1421]/10 border border-[#9B1421]/30 rounded-lg">
                      <div className="flex items-start gap-2">
                        <svg className="w-4 h-4 mt-0.5 text-rollops-accent flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                        </svg>
                        <div className="flex-1">
                          <p className="text-xs font-semibold text-rollops-accent mb-1">AI Summary</p>
                          <p className="text-xs text-text-primary leading-relaxed">{assessment.ai_summary}</p>
                        </div>
                      </div>
                    </div>
                  )}
                  {assessment.error_message && (
                    <div className="mt-2 p-2 bg-yellow-500/10 border border-yellow-500/30 rounded text-xs text-yellow-400">
                      <div className="flex items-start gap-2">
                        <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                        <div className="flex-1">
                          <strong>Assessment Issue:</strong> {assessment.error_message}
                          {assessment.error_message.includes('timeout') && (
                            <div className="mt-1 text-yellow-300/80">
                              This could be due to:
                              <ul className="list-disc list-inside mt-1 space-y-0.5">
                                <li>Website is slow or unreachable</li>
                                <li>Website is blocking automated requests</li>
                                <li>Network connectivity issues</li>
                                <li>Invalid or incorrect URL</li>
                              </ul>
                              <p className="mt-1">Please verify the URL is correct and try again.</p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                <div className="flex gap-2 ml-4">
                  <Button
                    onClick={() => setIsExpanded(!isExpanded)}
                    className="bg-[#363232] hover:bg-[#504A4A] text-text-primary"
                    size="sm"
                  >
                    {isExpanded ? (
                      <>
                        <ChevronUp className="w-4 h-4 mr-1" />
                        Hide Details
                      </>
                    ) : (
                      <>
                        <ChevronDown className="w-4 h-4 mr-1" />
                        View Details
                      </>
                    )}
                  </Button>
                  <Button
                    onClick={handlePrint}
                    className="bg-[#363232] hover:bg-[#504A4A] text-text-primary"
                    size="sm"
                    title="Print Assessment Report"
                  >
                    <Printer className="w-4 h-4" />
                  </Button>
                  <Button
                    onClick={handleDownloadPdf}
                    disabled={isGeneratingPdf}
                    className="bg-[#363232] hover:bg-[#504A4A] text-text-primary"
                    size="sm"
                    title="Download PDF Report"
                  >
                    {isGeneratingPdf ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                    ) : (
                      <Download className="w-4 h-4" />
                    )}
                  </Button>
                  <Button
                    onClick={handleCheckWebsite}
                    disabled={checkingUrl}
                    className="bg-[#363232] hover:bg-[#504A4A] text-text-primary"
                    size="sm"
                  >
                    {checkingUrl ? 'Checking...' : 'Re-check'}
                  </Button>
                </div>
              </div>
            </div>

            {/* Expanded Details */}
            {isExpanded && (
              <>
                {/* Performance Scores */}
                {(assessment.performance_score !== null || assessment.seo_score !== null) && (
                  <div className="mb-4 p-3 bg-[#363232] rounded-lg">
                    <div className="flex gap-4 text-xs text-text-secondary">
                      {assessment.performance_score !== null && (
                        <span>Performance: {assessment.performance_score}/100</span>
                      )}
                      {assessment.seo_score !== null && (
                        <span>SEO: {assessment.seo_score}/100</span>
                      )}
                      {assessment.accessibility_score !== null && (
                        <span>Accessibility: {assessment.accessibility_score}/100</span>
                      )}
                    </div>
                  </div>
                )}

            {/* Navigation Tabs */}
            <div className="mb-6 flex gap-2 border-b border-[#504A4A]">
              <button
                onClick={() => setActiveTab('basic')}
                className={`px-4 py-2 text-sm font-medium transition-colors border-b-2 ${
                  activeTab === 'basic'
                    ? 'border-[#9B1421] text-text-primary'
                    : 'border-transparent text-text-secondary hover:text-text-primary'
                }`}
              >
                <Shield className="w-4 h-4 inline mr-2" />
                Basic Checks
              </button>
              <button
                onClick={() => setActiveTab('features')}
                className={`px-4 py-2 text-sm font-medium transition-colors border-b-2 ${
                  activeTab === 'features'
                    ? 'border-[#9B1421] text-text-primary'
                    : 'border-transparent text-text-secondary hover:text-text-primary'
                }`}
              >
                <Zap className="w-4 h-4 inline mr-2" />
                Business Features
              </button>
              <button
                onClick={() => setActiveTab('seo')}
                className={`px-4 py-2 text-sm font-medium transition-colors border-b-2 ${
                  activeTab === 'seo'
                    ? 'border-[#9B1421] text-text-primary'
                    : 'border-transparent text-text-secondary hover:text-text-primary'
                }`}
              >
                <Search className="w-4 h-4 inline mr-2" />
                SEO Basics
              </button>
            </div>

            {/* Checklist - Tabbed Content */}
            <div className="space-y-3 mb-6 min-h-[200px]">
              {activeTab === 'basic' && (
                <>
                  {[
                    { label: 'URL Works', value: assessment.url_works },
                    { label: 'Loads Successfully', value: assessment.loads_successfully },
                    { label: 'Has Phone Number', value: assessment.has_phone_number, count: assessment.phone_numbers_found?.length },
                    { label: 'Has Email', value: assessment.has_email, count: assessment.emails_found?.length },
                    { label: 'Has Address', value: assessment.has_address },
                    { label: 'SSL/HTTPS Enabled', value: assessment.has_ssl },
                    { label: 'Mobile Friendly', value: assessment.mobile_friendly },
                  ].map((check) => (
                    <div key={check.label} className="flex items-center gap-3">
                      {check.value ? (
                        <CheckCircle className="w-5 h-5 text-green-400" />
                      ) : (
                        <XCircle className="w-5 h-5 text-red-400" />
                      )}
                      <span className="text-sm text-text-primary flex-1">{check.label}</span>
                      {check.count !== undefined && check.count > 0 && (
                        <span className="text-xs text-text-secondary">({check.count} found)</span>
                      )}
                    </div>
                  ))}
                </>
              )}
              
              {activeTab === 'features' && (
                <>
                  {[
                    { label: 'Social Media Links', value: assessment.has_social_links, count: assessment.social_links_found?.length },
                    { label: 'Online Booking', value: assessment.has_online_booking },
                    { label: 'Testimonials/Reviews', value: assessment.has_testimonials },
                    { label: 'Photo Gallery', value: assessment.has_photo_gallery },
                    { label: 'Blog/News Section', value: assessment.has_blog },
                  ].map((check) => (
                    <div key={check.label} className="flex items-center gap-3">
                      {check.value ? (
                        <CheckCircle className="w-5 h-5 text-green-400" />
                      ) : (
                        <XCircle className="w-5 h-5 text-red-400" />
                      )}
                      <span className="text-sm text-text-primary flex-1">{check.label}</span>
                      {check.count !== undefined && check.count > 0 && (
                        <span className="text-xs text-text-secondary">({check.count} found)</span>
                      )}
                    </div>
                  ))}
                </>
              )}
              
              {activeTab === 'seo' && (
                <>
                  {/* SEO Basics */}
                  <div className="mb-4 pb-4 border-b border-[#504A4A]">
                    <h4 className="text-xs font-semibold text-text-secondary mb-3 uppercase tracking-wide">SEO Basics</h4>
                    {[
                      { label: 'Meta Title', value: assessment.has_meta_title },
                      { label: 'Meta Description', value: assessment.has_meta_description },
                      { label: 'Meta Keywords', value: assessment.has_meta_keywords },
                    ].map((check) => (
                      <div key={check.label} className="flex items-center gap-3 mb-2">
                        {check.value ? (
                          <CheckCircle className="w-5 h-5 text-green-400" />
                        ) : (
                          <XCircle className="w-5 h-5 text-red-400" />
                        )}
                        <span className="text-sm text-text-primary">{check.label}</span>
                      </div>
                    ))}
                  </div>

                  {/* Local SEO Rankings */}
                  {assessment.seo_rankings && Array.isArray(assessment.seo_rankings) && assessment.seo_rankings.length > 0 ? (
                    <div>
                      <h4 className="text-xs font-semibold text-text-secondary mb-3 uppercase tracking-wide">Local Search Rankings</h4>
                      <div className="space-y-2">
                        {assessment.seo_rankings.map((ranking: any, index: number) => (
                          <div key={index} className="p-3 bg-[#2A2525] rounded-lg border border-[#504A4A]">
                            <div className="flex items-start justify-between mb-1">
                              <span className="text-sm text-text-primary font-medium">{ranking.keyword}</span>
                              {ranking.position ? (
                                <span className="text-xs font-bold text-green-400">#{ranking.position}</span>
                              ) : (
                                <span className="text-xs text-text-tertiary">Not in top 10</span>
                              )}
                            </div>
                            {ranking.total_results && (
                              <div className="text-xs text-text-tertiary mt-1">
                                {parseInt(ranking.total_results).toLocaleString()} total results
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                      <p className="text-xs text-text-tertiary mt-3">
                        Rankings checked: {new Date(assessment.seo_rankings[0]?.checked_at || assessment.assessment_date).toLocaleDateString()}
                      </p>
                    </div>
                  ) : (
                    <div className="text-center py-4 text-text-tertiary text-xs">
                      <p className="mb-2">Local SEO rankings not available</p>
                      <p className="text-[10px]">Requires Google Custom Search API setup</p>
                    </div>
                  )}
                </>
              )}
              
              {assessment.load_time_ms !== null && (
                <div className="flex items-center gap-3 mt-4 pt-2 border-t border-[#504A4A]">
                  <Clock className="w-5 h-5 text-text-secondary" />
                  <span className="text-sm text-text-primary">
                    Load Time: {(assessment.load_time_ms / 1000).toFixed(1)}s
                    {assessment.load_time_ms > 3000 && (
                      <span className="text-yellow-400 ml-2">⚠️ Slow</span>
                    )}
                  </span>
                </div>
              )}
            </div>

              </>
            )}

            {/* Call to Action */}
            <div className="mb-4 p-4 bg-[#9B1421]/10 border border-[#9B1421]/30 rounded-lg">
              <h3 className="text-sm font-semibold text-text-primary mb-2">
                Want help improving your website?
              </h3>
              <p className="text-xs text-text-secondary mb-3">
                Book a free website consultation to get personalized recommendations and support
              </p>
              <Button
                onClick={handleRequestConsultation}
                disabled={isRequestingConsultation}
                className="bg-[#9B1421] hover:bg-[#7A0F1A] text-white text-sm"
              >
                {isRequestingConsultation ? (
                  <span className="flex items-center gap-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                    Submitting...
                  </span>
                ) : (
                  'Book Free Consultation'
                )}
              </Button>
            </div>

            {/* Future Enhancements */}
            <div className="pt-4 border-t border-[#504A4A]">
              <FeatureRequestButton
                featureCategory="website_enhancements"
                featureName="AI-powered website analysis"
                description="Advanced analysis including calendar detection, photo analysis, blog posts, mobile responsiveness, and SEO"
              />
            </div>
          </>
        ) : (
          <div className="text-center py-8">
            <p className="text-text-secondary mb-4">No assessment available</p>
            {!websiteUrl ? (
              <p className="text-text-tertiary text-sm mb-4">
                Please add your website URL in company settings to enable assessment
              </p>
            ) : (
              <>
                <p className="text-text-tertiary text-sm mb-4">
                  Click below to assess your website: <strong className="text-text-primary">{websiteUrl}</strong>
                </p>
                <Button
                  onClick={handleCheckWebsite}
                  disabled={checkingUrl || !websiteUrl}
                  className="bg-[#9B1421] hover:bg-[#7A0F1A] text-white"
                >
                  {checkingUrl ? (
                    <span className="flex items-center gap-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                      Checking...
                    </span>
                  ) : (
                    'Check Website'
                  )}
                </Button>
              </>
            )}
          </div>
        )}
      </CardContent>
      
      {/* Print View */}
      {assessment && websiteUrl && (
        <div ref={printRef}>
          <WebsiteAssessmentPrint
            assessment={assessment}
            websiteUrl={websiteUrl}
            companyName={companyName || undefined}
          />
        </div>
      )}
    </Card>
  );
}
